// ============================================================
// 验证码输入组件
// ============================================================

import { useState, useEffect, useCallback } from 'react'
import { Input } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'

interface CaptchaInputProps {
  value?: { token: string; answer: string }
  onChange?: (value: { token: string; answer: string }) => void
  compact?: boolean // 紧凑模式
}

export default function CaptchaInput({ onChange, compact = false }: CaptchaInputProps) {
  const [question, setQuestion] = useState('')
  const [token, setToken] = useState('')
  const [answer, setAnswer] = useState('')
  const [loading, setLoading] = useState(false)

  // 获取验证码
  const fetchCaptcha = useCallback(async () => {
    setLoading(true)
    try {
      const response = await fetch('/api/captcha/get')
      const data = await response.json()
      if (data.code === 0) {
        setQuestion(data.data.question)
        setToken(data.data.token)
        setAnswer('')
        onChange?.({ token: data.data.token, answer: '' })
      }
    } catch (error) {
      console.error('获取验证码失败:', error)
    } finally {
      setLoading(false)
    }
  }, [onChange])

  // 初始化获取验证码
  useEffect(() => {
    fetchCaptcha()
  }, [fetchCaptcha])

  // 答案变化
  const handleAnswerChange = (val: string) => {
    setAnswer(val)
    onChange?.({ token, answer: val })
  }

  const isCompact = compact

  return (
    <div style={{ display: 'flex', gap: isCompact ? 8 : 12, alignItems: 'stretch' }}>
      {/* 左侧：数学题展示区 */}
      <div
        onClick={fetchCaptcha}
        title="点击刷新验证码"
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: isCompact ? 8 : 12,
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(236, 72, 153, 0.15) 100%)',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          borderRadius: isCompact ? 10 : 12,
          padding: isCompact ? '8px 12px' : '12px 20px',
          minHeight: isCompact ? 40 : 48,
          cursor: 'pointer',
          userSelect: 'none',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.5)'
          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(236, 72, 153, 0.2) 100%)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.3)'
          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(236, 72, 153, 0.15) 100%)'
        }}
      >
        {/* 装饰干扰线 */}
        <svg
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
        >
          <line x1="10%" y1="30%" x2="90%" y2="70%" stroke="rgba(139, 92, 246, 0.15)" strokeWidth="1" />
          <line x1="20%" y1="80%" x2="80%" y2="20%" stroke="rgba(236, 72, 153, 0.15)" strokeWidth="1" />
          <line x1="5%" y1="50%" x2="95%" y2="50%" stroke="rgba(139, 92, 246, 0.1)" strokeWidth="0.5" />
        </svg>

        {/* 数学题 */}
        <span
          style={{
            fontSize: isCompact ? 15 : 20,
            fontWeight: 700,
            color: '#FFFFFF',
            fontFamily: "'JetBrains Mono', monospace",
            letterSpacing: isCompact ? 1 : 2,
            textShadow: '0 0 10px rgba(139, 92, 246, 0.3)',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {question || '加载中...'}
        </span>

        {/* 刷新图标 */}
        <ReloadOutlined
          spin={loading}
          style={{
            color: 'rgba(139, 92, 246, 0.6)',
            fontSize: isCompact ? 12 : 14,
            position: 'relative',
            zIndex: 1,
          }}
        />
      </div>

      {/* 右侧：答案输入框 */}
      <div style={{ width: isCompact ? 80 : 100 }}>
        <Input
          value={answer}
          onChange={(e) => handleAnswerChange(e.target.value.replace(/[^\d-]/g, ''))}
          placeholder="?"
          style={{
            width: '100%',
            height: isCompact ? 40 : 48,
            background: 'transparent',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: isCompact ? 10 : 12,
            color: '#FFFFFF',
            textAlign: 'center',
            fontSize: isCompact ? 15 : 18,
            fontWeight: 600,
            fontFamily: "'JetBrains Mono', monospace",
          }}
        />
      </div>
    </div>
  )
}
